import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { JarvisCard } from '@/components/JarvisCard'
import { JarvisProfileCard } from '@/components/JarvisProfileCard'
import { PersonaPicker } from '@/components/PersonaPicker'
import { BriefingSettings } from '@/components/BriefingSettings'
import { FacebookIntegrationCard } from '@/components/FacebookIntegrationCard'
import { UsageSettings } from '@/components/UsageSettings'
import { VibeSelectorFull } from '@/components/VibeSelectorFull'
import { useToast } from '@/components/Toast'
import { apiClient, unwrap } from '@/lib/apiClient'
import { HAS_AUTH } from '@/lib/auth'
import { usePersona } from '@/lib/personas'
import { effectiveVibe, type Vibe, type VibePersona } from '@/lib/vibes'
import type { User } from '@/types'

interface SettingsResponse {
  settings: User['settings']
  // Top-level User fields surfaced by GET /api/settings (see api/.../getSettings.ts).
  // Lives outside `settings` because it's a top-level User field, not a settings sub-key.
  approvedCallerNumbers?: string[]
}

// Auto-scroll into view when the hash matches one of our anchors — supports
// the "Change your vibe →" link from the Command Center landing on #voice.
function useHashScroll(): void {
  useEffect(() => {
    const hash = window.location.hash.replace('#', '')
    if (!hash) return
    // Defer one tick so the section has mounted
    const t = window.setTimeout(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 50)
    return () => window.clearTimeout(t)
  }, [])
}

export function Settings() {
  useHashScroll()
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery<SettingsResponse>({
    queryKey: ['settings'],
    queryFn: () => unwrap<SettingsResponse>(apiClient.get('/settings')),
    enabled: HAS_AUTH,
  })

  const [companyName, setCompanyName] = useState('')
  const [companyDescription, setCompanyDescription] = useState('')
  const [emailNotifications, setEmailNotifications] = useState(true)
  const [slackWebhook, setSlackWebhook] = useState('')

  // Banner driven by URL params after the Facebook OAuth callback redirect.
  // Strip the params after reading so a refresh doesn't keep re-showing it.
  const initialFbBanner = useMemo(() => {
    if (typeof window === 'undefined') return null
    const params = new URLSearchParams(window.location.search)
    if (params.get('fb_connected') === 'true') {
      return { kind: 'success' as const, text: 'Facebook Ads connected. Jarvis will start syncing within 4 hours.' }
    }
    const err = params.get('fb_error')
    if (err) {
      return { kind: 'error' as const, text: `Facebook connection failed: ${err}` }
    }
    return null
  }, [])
  const [fbBanner, setFbBanner] = useState(initialFbBanner)
  useEffect(() => {
    if (!initialFbBanner) return
    const url = new URL(window.location.href)
    url.searchParams.delete('fb_connected')
    url.searchParams.delete('fb_error')
    window.history.replaceState({}, '', url.toString())
  }, [initialFbBanner])
  const dismissFbBanner = () => setFbBanner(null)

  useEffect(() => {
    if (!data) return
    setCompanyName(data.settings.companyName ?? '')
    setCompanyDescription(data.settings.companyDescription ?? '')
    setEmailNotifications(data.settings.notificationsEmail ?? true)
    setSlackWebhook(data.settings.slackWebhookUrl ?? '')
  }, [data])

  const save = useMutation({
    mutationFn: () =>
      unwrap(
        apiClient.patch('/settings', {
          companyName,
          companyDescription,
          notificationsEmail: emailNotifications,
          notificationsSlack: Boolean(slackWebhook),
          slackWebhookUrl: slackWebhook || null,
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settings'] }),
  })

  const approvedNumbers = data?.approvedCallerNumbers ?? []
  const showAllowlistNudge = HAS_AUTH && data !== undefined && approvedNumbers.length === 0

  return (
    <div className="space-y-6 max-w-3xl">
      <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>

      {showAllowlistNudge && (
        <div className="flex items-center gap-3 border border-jarvis-amber/40 bg-jarvis-amber/5 rounded-xl px-4 py-3 text-sm">
          <span className="text-jarvis-amber text-base">⚠</span>
          <span className="text-jarvis-muted flex-1">
            Your AI agent has no approved numbers.{' '}
            <span className="text-jarvis-text">Anyone who calls your line can reach it.</span>
          </span>
          <a
            href="#phone-security"
            className="text-jarvis-blue text-xs hover:underline whitespace-nowrap"
            onClick={() => {
              // Hash change won't refire useHashScroll, so scroll directly.
              setTimeout(() => {
                document.getElementById('phone-security')?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start',
                })
              }, 0)
            }}
          >
            Add a number →
          </a>
        </div>
      )}

      <PersonaPicker />

      <JarvisProfileCard />

      <JarvisCard>
        <h2 className="text-xs uppercase tracking-widest text-jarvis-muted mb-4">
          Company
        </h2>
        <div className="space-y-4">
          <Field label="Company name">
            <input
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="bg-jarvis-surface-2 border border-jarvis-border rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:border-jarvis-blue/60"
            />
          </Field>
          <Field label="Description">
            <textarea
              rows={2}
              value={companyDescription}
              onChange={(e) => setCompanyDescription(e.target.value)}
              className="bg-jarvis-surface-2 border border-jarvis-border rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:border-jarvis-blue/60"
            />
          </Field>
        </div>
      </JarvisCard>

      <JarvisCard>
        <h2 className="text-xs uppercase tracking-widest text-jarvis-muted mb-4">
          Notifications
        </h2>
        <label className="flex items-center gap-3 text-sm">
          <input
            type="checkbox"
            checked={emailNotifications}
            onChange={(e) => setEmailNotifications(e.target.checked)}
            className="accent-jarvis-blue"
          />
          Email me the morning briefing
        </label>
        <Field label="Slack webhook URL" className="mt-4">
          <input
            value={slackWebhook}
            onChange={(e) => setSlackWebhook(e.target.value)}
            placeholder="https://hooks.slack.com/…"
            className="bg-jarvis-surface-2 border border-jarvis-border rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:border-jarvis-blue/60"
          />
        </Field>
      </JarvisCard>

      <div id="usage">
        <UsageSettings />
      </div>

      <VoiceCallSettings />

      <PhoneSecurityCard />

      <BriefingSettings />

      <FacebookIntegrationCard
        banner={fbBanner}
        onDismissBanner={dismissFbBanner}
      />

      <JarvisCard>
        <h2 className="text-xs uppercase tracking-widest text-jarvis-muted mb-4">
          Other Integrations
        </h2>
        <div className="space-y-2 text-sm">
          <IntegrationRow name="Google Calendar" connected={false} />
          <IntegrationRow name="Stripe" connected={false} />
          <IntegrationRow name="Slack" connected={Boolean(slackWebhook)} />
        </div>
      </JarvisCard>

      <div className="flex justify-end">
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending || isLoading}
          className="bg-jarvis-blue hover:bg-jarvis-blue-bright disabled:opacity-50 px-5 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          {save.isPending ? 'Saving…' : 'Save Changes'}
        </button>
      </div>
    </div>
  )
}

function Field({
  label,
  children,
  className,
}: {
  label: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <label className={`block ${className ?? ''}`}>
      <span className="text-xs uppercase tracking-widest text-jarvis-muted mb-1.5 block">
        {label}
      </span>
      {children}
    </label>
  )
}

// =============================================================================
// VoiceCallSettings — vibe picker + clap-to-call toggle + custom greeting
// =============================================================================
// Persists to user.settings via PATCH /api/settings. The Command Center reads
// the same fields and clamps the active vibe to whatever the current persona
// allows. See docs/35_CALL_DASHBOARD.md.

interface VoiceSettings {
  callVibe?: Vibe
  callGreeting?: string | null
  clapToCallEnabled?: boolean
  clapSensitivity?: number
}

function VoiceCallSettings() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const { personaId, persona } = usePersona()

  const { data } = useQuery<SettingsResponse>({
    queryKey: ['settings'],
    queryFn: () => unwrap<SettingsResponse>(apiClient.get('/settings')),
    enabled: HAS_AUTH,
  })
  const stored = data?.settings as (User['settings'] & VoiceSettings) | undefined

  const [vibe, setVibe] = useState<Vibe>('professional')
  const [customGreeting, setCustomGreeting] = useState('')
  const [clapEnabled, setClapEnabled] = useState(false)
  const [sensitivity, setSensitivity] = useState(0.35)

  useEffect(() => {
    if (!stored) return
    setVibe(stored.callVibe ?? 'professional')
    setCustomGreeting(stored.callGreeting ?? '')
    setClapEnabled(stored.clapToCallEnabled ?? false)
    setSensitivity(stored.clapSensitivity ?? 0.35)
  }, [stored])

  const resolvedVibe = effectiveVibe(personaId as VibePersona, vibe)

  const save = useMutation({
    mutationFn: () =>
      unwrap(
        apiClient.patch('/settings', {
          callVibe: resolvedVibe,
          callGreeting: customGreeting.trim() || null,
          clapToCallEnabled: clapEnabled,
          clapSensitivity: sensitivity,
        } satisfies VoiceSettings),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] })
      queryClient.invalidateQueries({ queryKey: ['user', 'me'] })
      toast.success('Voice settings saved', `${persona.wordmark} will pick up the new vibe on the next call.`)
    },
    onError: (e) => toast.error('Could not save voice settings', (e as Error).message),
  })

  return (
    <JarvisCard id="voice">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xs uppercase tracking-widest text-jarvis-muted">
          Voice
        </h2>
        <span className="text-xs text-jarvis-muted">
          Active persona: <span className="text-jarvis-text">{persona.wordmark}</span>
        </span>
      </div>
      <p className="text-xs text-jarvis-muted mb-4">
        Pick how {persona.wordmark} opens an in-app call. The greeting changes a little
        each call — it's a vibe, not a script.
      </p>

      <h3 className="text-sm font-semibold mb-2">Greeting vibe</h3>
      <VibeSelectorFull
        currentVibe={resolvedVibe}
        personaId={personaId as VibePersona}
        onChange={setVibe}
        customGreeting={customGreeting}
        onCustomGreetingChange={setCustomGreeting}
      />

      <div className="mt-6 border-t border-jarvis-border pt-5">
        <h3 className="text-sm font-semibold mb-2">Clap to call</h3>
        <label className="flex items-center gap-3 text-sm">
          <input
            type="checkbox"
            checked={clapEnabled}
            onChange={(e) => setClapEnabled(e.target.checked)}
            className="accent-jarvis-blue"
          />
          <span>
            Double-clap your hands to start a call. Useful when you're hands-on and don't
            want to click. May trigger in noisy environments — tune sensitivity below.
          </span>
        </label>
        {clapEnabled && (
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs text-jarvis-muted mb-1.5">
              <span>Sensitivity</span>
              <span className="text-jarvis-text">
                {sensitivity <= 0.25 ? 'High' : sensitivity <= 0.45 ? 'Medium' : 'Low'}
              </span>
            </div>
            <input
              type="range"
              min={0.1}
              max={0.9}
              step={0.05}
              value={sensitivity}
              onChange={(e) => setSensitivity(parseFloat(e.target.value))}
              className="w-full accent-jarvis-blue"
            />
            <div className="flex justify-between text-xs text-jarvis-muted mt-1">
              <span>High (soft claps)</span>
              <span>Low (loud claps only)</span>
            </div>
          </div>
        )}
        <p className="text-xs text-jarvis-muted mt-3">
          Clap detection never records audio — it only watches for volume spikes locally in your browser.
        </p>
      </div>

      <div className="flex justify-end mt-6">
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="bg-jarvis-blue hover:bg-jarvis-blue-bright disabled:opacity-50 px-5 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          {save.isPending ? 'Saving…' : save.isSuccess ? 'Saved' : 'Save voice settings'}
        </button>
      </div>
    </JarvisCard>
  )
}

// =============================================================================
// PhoneSecurityCard — caller allowlist
// =============================================================================
// Only numbers in `approvedCallerNumbers` can reach the agent (enforced in
// packages/api/src/functions/voice/voiceIncomingCall.ts). Empty list = open.
// See docs/phone_security.md.

// Normalize user input to E.164 so the digits-only allowlist compare matches
// what ACS actually sends (always full E.164 with country code). Mirrored
// server-side in updateSettings.ts as defense in depth — keep the two in sync.
//   "3125550100"        → "+13125550100"     (10 digits → assume US)
//   "13125550100"       → "+13125550100"     (11 digits starting with 1 → US)
//   "(312) 555-0100"    → "+13125550100"
//   "+44 7911 123456"   → "+447911123456"    (any leading + → trust as international)
//   "555"               → null               (too short, ambiguous)
function toE164(input: string): string | null {
  const trimmed = input.trim()
  if (!trimmed) return null
  if (trimmed.startsWith('+')) {
    const digits = trimmed.slice(1).replace(/\D/g, '')
    // E.164 max 15 digits, min 7 (shortest real international numbers)
    if (digits.length < 7 || digits.length > 15) return null
    return '+' + digits
  }
  const digits = trimmed.replace(/\D/g, '')
  if (digits.length === 10) return '+1' + digits
  if (digits.length === 11 && digits.startsWith('1')) return '+' + digits
  return null
}

// Pretty-print US numbers as "+1 (312) 555-0100", leave international as-is.
function formatE164ForDisplay(e164: string): string {
  if (e164.startsWith('+1') && e164.length === 12) {
    const d = e164.slice(2)
    return `+1 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
  }
  return e164
}

function PhoneSecurityCard() {
  const queryClient = useQueryClient()
  const toast = useToast()

  const { data } = useQuery<SettingsResponse>({
    queryKey: ['settings'],
    queryFn: () => unwrap<SettingsResponse>(apiClient.get('/settings')),
    enabled: HAS_AUTH,
  })

  const [numbers, setNumbers] = useState<string[]>([])
  const [newNumber, setNewNumber] = useState('')
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    if (!data) return
    setNumbers(data.approvedCallerNumbers ?? [])
    setDirty(false)
  }, [data])

  const digitKey = (n: string) => n.replace(/\D/g, '')

  const addNumber = () => {
    const normalized = toE164(newNumber)
    if (!normalized) {
      toast.error(
        'Enter a valid number',
        '10-digit US number (e.g. 312-555-0100) or full international with + and country code.',
      )
      return
    }
    const key = digitKey(normalized)
    if (numbers.some((n) => digitKey(n) === key)) {
      toast.error('Already on the list', formatE164ForDisplay(normalized))
      setNewNumber('')
      return
    }
    setNumbers((prev) => [...prev, normalized])
    setNewNumber('')
    setDirty(true)
  }

  const removeNumber = (n: string) => {
    setNumbers((prev) => prev.filter((x) => x !== n))
    setDirty(true)
  }

  const save = useMutation({
    mutationFn: () =>
      unwrap(
        apiClient.patch('/settings', {
          approvedCallerNumbers: numbers,
        }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] })
      queryClient.invalidateQueries({ queryKey: ['user', 'me'] })
      setDirty(false)
      toast.success('Phone security saved', 'Only approved numbers reach your agent now.')
    },
    onError: (e) => toast.error('Could not save', (e as Error).message),
  })

  return (
    <JarvisCard id="phone-security">
      <h2 className="text-xs uppercase tracking-widest text-jarvis-muted mb-1">
        Phone Security
      </h2>
      <p className="text-xs text-jarvis-muted mb-4">
        Only these numbers can reach your AI agent. Anyone not on this list will
        not connect. Leave empty to allow any caller.
      </p>

      <div className="space-y-2 mb-3">
        {numbers.map((n) => (
          <div
            key={n}
            className="flex items-center justify-between bg-jarvis-surface-2 border border-jarvis-border rounded-lg px-3 py-2 text-sm"
          >
            <span className="text-jarvis-text font-mono">{formatE164ForDisplay(n)}</span>
            <button
              onClick={() => removeNumber(n)}
              className="text-jarvis-muted hover:text-jarvis-red transition-colors text-xs ml-4"
            >
              Remove
            </button>
          </div>
        ))}

        {numbers.length === 0 && (
          <div className="flex items-start gap-2 text-xs text-jarvis-muted border border-dashed border-jarvis-border rounded-lg px-3 py-3">
            <span className="text-jarvis-amber">⚠</span>
            <span>
              No approved numbers — anyone who dials your line can reach your agent.
              Add your number below.
            </span>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <input
          value={newNumber}
          onChange={(e) => setNewNumber(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              addNumber()
            }
          }}
          placeholder="(312) 555-0100"
          inputMode="tel"
          autoComplete="tel"
          className="bg-jarvis-surface-2 border border-jarvis-border rounded-lg px-3 py-2 text-sm flex-1 focus:outline-none focus:border-jarvis-blue/60 font-mono"
        />
        <button
          onClick={addNumber}
          className="bg-jarvis-surface-2 hover:bg-jarvis-surface border border-jarvis-border px-4 py-2 rounded-lg text-sm transition-colors"
        >
          Add
        </button>
      </div>
      <p className="text-xs text-jarvis-muted mt-2">
        US numbers — just enter 10 digits, we'll add{' '}
        <span className="text-jarvis-text font-mono">+1</span> for you.
        International? Start with{' '}
        <span className="text-jarvis-text font-mono">+</span> and your country
        code (e.g. <span className="font-mono">+447911123456</span>).
      </p>

      <div className="flex justify-end mt-6">
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending || !dirty}
          className="bg-jarvis-blue hover:bg-jarvis-blue-bright disabled:opacity-50 px-5 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          {save.isPending
            ? 'Saving…'
            : !dirty && save.isSuccess
              ? 'Saved'
              : 'Save security settings'}
        </button>
      </div>
    </JarvisCard>
  )
}

function IntegrationRow({ name, connected }: { name: string; connected: boolean }) {
  return (
    <div className="flex items-center justify-between border-b border-jarvis-border/50 py-2 last:border-0">
      <span>{name}</span>
      <span
        className={`text-xs flex items-center gap-1.5 ${connected ? 'text-jarvis-green' : 'text-jarvis-muted'}`}
      >
        <span
          className={`inline-block w-1.5 h-1.5 rounded-full ${connected ? 'bg-jarvis-green' : 'bg-jarvis-muted'}`}
        />
        {connected ? 'Connected' : 'Not connected'}
      </span>
    </div>
  )
}
